import { useEffect, useRef, useState, type FormEvent, type ReactNode } from "react";
import { Check, RefreshCw, X } from "lucide-react";
import { usePage, type SectionProps } from "./context";
import { Band, Head, cn, rv, str } from "./ui";
import { aiNav, aiNavAnchors, defaultPersona } from "@/mocks/aiassist";
import { snippets } from "@/mocks/snippets";
import { goAnchor } from "./app-nav";
import { t } from "@/lib/i18n";

/* Живой макет «AI Assist»: ассистент отвечает на основе источников знаний и шаблонов.
   Всё работает в макете — переключатели, персона, тест ассистента и переход к источникам знаний;
   настоящие диалоги и база не затрагиваются. */

type Msg = { id: number; role: "user" | "assistant"; text: string; source?: string; general?: boolean };

const SNIPPET_COUNT = (snippets as unknown[]).length;
const INPUT =
  "w-full box-border min-w-0 rounded-btn border border-border bg-background px-3 text-[13px] text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/25";

// короткие ответы для теста: ищем ключевые слова в вопросе
const ANSWERS = [
  {
    test: /цен|прайс|стоим|тариф|сколько/i,
    text: t("Тариф «Команда» — 1 490 ₽ за пользователя в месяц, при оплате за год скидка 20%. Могу собрать расчёт под вашу команду: подскажите, сколько человек будет работать в Hub."),
    source: t("Шаблон «Прайс и условия»"),
  },
  {
    test: /счёт|счет|оплат|реквизит|платёж|платеж/i,
    text: t("Счёт готов и приложен к сообщению: оплатить можно по ссылке из документа, назначение платежа менять не нужно. После оплаты пришлите платёжное поручение — сразу закроем доступ."),
    source: t("Шаблон «Реквизиты для оплаты»"),
  },
  {
    test: /подключ|qr|номер|срок|настрой/i,
    text: t("Номер WhatsApp подключается по QR-коду за 2–3 минуты, Telegram — по токену бота. Если нужна помощь с настройкой, инженер подключится к созвону в течение рабочего дня."),
    source: t("Шаблон «Сроки подключения»"),
  },
  {
    test: /возврат|брак|претенз|обмен/i,
    text: t("Возврат оформляем в течение 14 дней с момента оплаты: напишите номер договора и причину, мы вышлем форму заявления и вернём деньги на тот же счёт."),
    source: t("Источник: регламент возвратов"),
  },
];

function Toggle({ on, onChange, label }: { on: boolean; onChange: (v: boolean) => void; label: string }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={label}
      onClick={() => onChange(!on)}
      className={cn(
        "relative h-6 w-11 flex-none cursor-pointer rounded-full border transition",
        on ? "border-primary bg-primary" : "border-border bg-foreground/15",
      )}
    >
      <span
        aria-hidden
        className={cn("absolute top-0.5 size-[18px] rounded-full bg-white transition-all", on ? "left-[22px]" : "left-0.5")}
      />
    </button>
  );
}

function SettingRow({ title, text, children }: { title: string; text: string; children?: ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-3 rounded-btn border border-border bg-background/50 p-3.5">
      <div className="min-w-0">
        <p className="text-[13px] font-semibold leading-snug">{title}</p>
        <p className="mt-1 text-[11.5px] leading-relaxed text-muted-foreground">{text}</p>
      </div>
      {children}
    </div>
  );
}

function AiAssistDemo({ title, subtitle }: SectionProps) {
  const { brand } = usePage();
  const [outside, setOutside] = useState(true);
  const [useSnippets, setUseSnippets] = useState(true);
  const [persona, setPersona] = useState(defaultPersona);
  const [savedPersona, setSavedPersona] = useState(defaultPersona);
  const [testOpen, setTestOpen] = useState(false);
  const [messages, setMessages] = useState<Msg[]>([
    {
      id: 1,
      role: "assistant",
      text: t("Здравствуйте! Я AI Assist. Спросите что угодно о тарифах, оплате или подключении — я отвечу по источникам знаний вашей компании."),
      source: t("Приветствие"),
    },
  ]);
  const [input, setInput] = useState("");
  const [pending, setPending] = useState(false);
  const [toast, setToast] = useState("");
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const chatRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => () => clearTimeout(timer.current), []);

  useEffect(() => {
    if (chatRef.current) chatRef.current.scrollTop = chatRef.current.scrollHeight;
  }, [messages, pending, testOpen]);

  const flash = (text: string) => {
    setToast(text);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setToast(""), 4200);
  };

  const onNav = (id: string) => {
    if (id === "ai-assist") return;
    const anchor = aiNavAnchors[id];
    if (anchor) {
      goAnchor(anchor);
      return;
    }
    flash(t("Раздел «{label}» в демо не открывается", { label: aiNav.flatMap((g) => g.items).find((x) => x.id === id)?.label ?? "" }));
  };

  const toKnowledge = () => {
    setTestOpen(false);
    goAnchor("#ai-knowledge");
    flash(t("Открыли источники знаний — туда загружаются документы, ссылки и инструкции"));
  };

  const answerFor = (q: string): Omit<Msg, "id" | "role"> => {
    const hit = ANSWERS.find((a) => a.test.test(q));
    if (hit) {
      if (useSnippets) return { text: hit.text, source: hit.source };
      return {
        text: t("Шаблоны сейчас не используются как источник знаний, поэтому ответ собран по общим знаниям модели. Включите «Использовать шаблоны», чтобы ассистент опирался на сохранённые ответы."),
        source: t("Общие знания"),
        general: true,
      };
    }
    if (outside) {
      return {
        text: t("В источниках знаний нет точного ответа, поэтому ассистент ответил по общим знаниям модели. Проверьте формулировку перед отправкой клиенту."),
        source: t("Общие знания"),
        general: true,
      };
    }
    return {
      text: t("В загруженных источниках знаний нет ответа на этот вопрос. Добавьте документ, ссылку или текст-инструкцию — и ассистент сможет отвечать по ним."),
      source: t("Нет источника"),
      general: true,
    };
  };

  const send = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const text = input.trim();
    if (!text || pending) return;
    setMessages((prev) => [...prev, { id: Date.now(), role: "user", text }]);
    setInput("");
    setPending(true);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      setMessages((prev) => [...prev, { id: Date.now() + 1, role: "assistant", ...answerFor(text) }]);
      setPending(false);
    }, 700);
  };

  const dirtyPersona = persona.trim() !== savedPersona.trim();
  const canSave = persona.trim().length >= 20 && dirtyPersona;

  return (
    <Band id="ai-assist" tone="default">
      <Head title={title} subtitle={subtitle} />
      <div {...rv()} className="flash-card relative overflow-hidden rounded-card border border-border bg-card !p-0">
        {/* полоска «окна приложения» */}
        <div className="flex flex-wrap items-center gap-2.5 border-b border-border px-4 py-3">
          <span className="flex gap-1.5" aria-hidden>
            <span className="size-2.5 rounded-full bg-foreground/20" />
            <span className="size-2.5 rounded-full bg-foreground/20" />
            <span className="size-2.5 rounded-full bg-foreground/20" />
          </span>
          <span className="flash-mono ms-1 truncate text-[11px] text-muted-foreground">
            {str(brand) || "Hub"} / {t("настройки → входящие → AI Assist")}
          </span>
          <span className="ms-auto inline-flex items-center gap-2 whitespace-nowrap text-[11px] text-muted-foreground">
            <span className="size-1.5 rounded-full bg-primary" aria-hidden />
            {t("AI Assist включён")}
          </span>
        </div>

        <div className="grid lg:grid-cols-[14.5rem_minmax(0,1fr)]">
          {/* ── настройки ── */}
          <div className="hidden flex-col gap-4 border-border p-3 lg:flex lg:border-e">
            {aiNav.map((g) => (
              <div key={g.group} className="flex flex-col gap-0.5">
                <p className="px-2 pb-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">{g.group}</p>
                {g.items.map((it) => {
                  const active = it.id === "ai-assist";
                  return (
                    <button
                      key={it.id}
                      type="button"
                      onClick={() => onNav(it.id)}
                      aria-current={active ? "page" : undefined}
                      className={cn(
                        "flex cursor-pointer items-center gap-2.5 rounded-btn px-2.5 py-2 text-start text-[12.5px] font-medium transition",
                        active ? "bg-primary/15 text-primary" : "text-muted-foreground hover:bg-muted hover:text-foreground",
                      )}
                    >
                      <i className={cn(it.icon, "text-base leading-none")} aria-hidden />
                      <span className="min-w-0 flex-1 truncate">{it.label}</span>
                      {it.badge ? (
                        <span className="rounded-full bg-amber-400/20 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-amber-600">{it.badge}</span>
                      ) : null}
                    </button>
                  );
                })}
              </div>
            ))}

            <div className="mt-auto rounded-btn border border-border bg-background/60 p-3">
              <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-primary">
                <i className="ri-shield-check-line text-sm leading-none" aria-hidden />
                {t("Доступы")}
              </p>
              <p className="mt-1.5 text-[11px] leading-relaxed text-muted-foreground">
                {t("AI Assist и источники знаний настраивает администратор. Продавцы получают готовые ответы и проверяют их перед отправкой.")}
              </p>
            </div>
          </div>

          {/* ── панель AI Assist ── */}
          <div className="min-w-0 p-3.5 md:p-5">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <h3 className="flex items-center gap-2 text-base font-semibold leading-snug">
                  <i className="ri-sparkling-line text-lg leading-none text-primary" aria-hidden />
                  AI Assist
                </h3>
                <p className="mt-1 max-w-xl text-[12px] leading-relaxed text-muted-foreground">
                  {t("Мгновенные ответы на основе источников знаний вашей компании.")}
                </p>
              </div>
              <button
                type="button"
                onClick={() => flash(t("Демо-режим: подробная справка по AI Assist в макете недоступна"))}
                className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap text-[12px] font-semibold text-primary transition hover:opacity-80"
              >
                <i className="ri-book-open-line text-sm leading-none" aria-hidden />
                {t("Узнать больше")}
              </button>
            </div>

            <div className="mt-3 flex gap-1.5 overflow-x-auto pb-1 lg:hidden">
              {aiNav.flatMap((g) => g.items).map((it) => (
                <button
                  key={it.id}
                  type="button"
                  onClick={() => onNav(it.id)}
                  className={cn(
                    "inline-flex flex-none cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 py-1 text-[11px] font-semibold transition",
                    it.id === "ai-assist" ? "border-primary bg-primary text-primary-foreground" : "border-border text-muted-foreground hover:text-foreground",
                  )}
                >
                  <i className={it.icon} aria-hidden />
                  {it.label}
                </button>
              ))}
            </div>

            <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
              <button
                type="button"
                onClick={() => {
                  setTestOpen(true);
                  setMessages((prev) => (prev.length === 1 ? prev : prev.slice(0, 1)));
                }}
                className="inline-flex h-9 cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-btn bg-primary px-3.5 text-[12px] font-semibold text-primary-foreground transition hover:brightness-110"
              >
                <i className="ri-sparkling-line" aria-hidden />
                {t("Протестировать AI Assist")}
              </button>
              <button
                type="button"
                onClick={toKnowledge}
                className="inline-flex h-9 cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-btn border border-border px-3.5 text-[12px] font-semibold text-muted-foreground transition hover:text-foreground"
              >
                <i className="ri-book-open-line" aria-hidden />
                {t("Источники знаний")}
              </button>
            </div>

            <div className="mt-4 grid gap-4 xl:grid-cols-[minmax(0,1fr)_16rem]">
              <div className="flex min-w-0 flex-col gap-2.5">
                <SettingRow
                  title={t("Ответы вне источников знаний")}
                  text={t("Если включено, ассистент отвечает по общим знаниям модели, как ChatGPT. Выключите — и он будет отвечать только по вашим документам.")}
                >
                  <Toggle on={outside} onChange={setOutside} label={t("Ответы вне источников знаний")} />
                </SettingRow>

                <SettingRow
                  title={t("Использовать шаблоны как источник")}
                  text={t("Все сохранённые ответы становятся источником знаний: сейчас их {n}. Ассистент подставит их дословно.", { n: SNIPPET_COUNT })}
                >
                  <Toggle on={useSnippets} onChange={setUseSnippets} label={t("Использовать шаблоны как источник")} />
                </SettingRow>

                <div className="rounded-btn border border-border bg-background/50 p-3.5">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-[13px] font-semibold leading-snug">{t("Персона AI Assist")}</p>
                      <p className="mt-1 text-[11.5px] leading-relaxed text-muted-foreground">
                        {t("Ассистент использует эту персону, чтобы отвечать голосом бренда, опираясь на источники знаний.")}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setPersona(defaultPersona);
                        setSavedPersona(defaultPersona);
                        flash(t("Формулировка персоны сброшена к стандартной"));
                      }}
                      className="inline-flex h-7 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-btn border border-border px-2.5 text-[11px] font-semibold text-muted-foreground transition hover:text-foreground"
                    >
                      <RefreshCw className="size-3.5" aria-hidden />
                      {t("Сбросить")}
                    </button>
                  </div>

                  <label className="mt-2.5 block">
                    <span className="sr-only">{t("Текст персоны AI Assist")}</span>
                    <textarea
                      value={persona}
                      onChange={(e) => setPersona(e.target.value.slice(0, 600))}
                      rows={5}
                      className={cn(INPUT, "h-auto min-h-[104px] resize-y py-2.5 leading-relaxed")}
                    />
                  </label>

                  <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
                    <span className="text-[10.5px] text-muted-foreground">
                      {persona.trim().length}/600 · {dirtyPersona ? t("есть несохранённые изменения") : t("сохранено")}
                    </span>
                    <button
                      type="button"
                      disabled={!canSave}
                      onClick={() => {
                        setSavedPersona(persona);
                        flash(t("Персона сохранена — ассистент будет отвечать в этом тоне"));
                      }}
                      className="inline-flex h-8 cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-btn bg-primary px-3 text-[12px] font-semibold text-primary-foreground transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      <Check className="size-3.5" aria-hidden />
                      {t("Сохранить персону")}
                    </button>
                  </div>
                </div>
              </div>

              {/* ── превью ответа в диалоге ── */}
              <div className="min-w-0">
                <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">{t("Как это в диалоге")}</p>
                <div className="mt-2 overflow-hidden rounded-btn border border-border bg-background/60">
                  <div className="flex items-center gap-2 border-b border-border px-2.5 py-2">
                    <span className="grid size-6 flex-none place-items-center rounded-full bg-primary/15 text-[10px] font-bold text-primary" aria-hidden>
                      {t("ОС")}
                    </span>
                    <span className="min-w-0 flex-1 truncate text-[11px] font-semibold">{t("Ольга Соколова")}</span>
                    <i className="ri-whatsapp-line text-[13px] leading-none text-primary" aria-hidden />
                  </div>
                  <div className="flex flex-col gap-2 p-2.5">
                    <p className="max-w-[92%] rounded-btn rounded-ss-sm bg-muted px-2.5 py-1.5 text-[11px] leading-relaxed text-foreground">
                      {t("Здравствуйте! Сколько стоит тариф для команды из 12 человек?")}
                    </p>
                    <div className="max-w-[96%] self-end rounded-btn rounded-se-sm border border-primary/30 bg-primary/10 px-2.5 py-1.5">
                      <p className="flex items-center gap-1 text-[9px] font-bold uppercase tracking-wide text-primary">
                        <i className="ri-sparkling-line text-[10px] leading-none" aria-hidden />
                        AI Assist
                      </p>
                      <p className="mt-1 text-[11px] leading-relaxed text-foreground">
                        {t("Для 12 человек — 17 880 ₽ в месяц по тарифу «Команда». При оплате за год скидка 20%: 171 648 ₽ за год. Собрать расчёт?")}
                      </p>
                    </div>
                    <div className="flex gap-1.5 self-end">
                      <button
                        type="button"
                        onClick={() => flash(t("Ответ вставлен в поле сообщения — его можно поправить перед отправкой"))}
                        className="inline-flex h-7 cursor-pointer items-center gap-1 whitespace-nowrap rounded-btn bg-primary px-2 text-[10.5px] font-semibold text-primary-foreground transition hover:brightness-110"
                      >
                        {t("Вставить")}
                      </button>
                      <button
                        type="button"
                        onClick={() => flash(t("Демо-режим: ответ можно отредактировать в настоящем окне Hub"))}
                        className="inline-flex h-7 cursor-pointer items-center whitespace-nowrap rounded-btn border border-border px-2 text-[10.5px] font-semibold text-muted-foreground transition hover:text-foreground"
                      >
                        {t("Изменить")}
                      </button>
                    </div>
                  </div>
                </div>
                <p className="mt-2 text-[11px] leading-relaxed text-muted-foreground">
                  {t("Загрузите документы и ссылки в")}{" "}
                  <button type="button" onClick={toKnowledge} className="cursor-pointer font-semibold text-primary hover:underline">
                    {t("источниках знаний")}
                  </button>
                  {t(", чтобы AI Assist отвечал точно и по вашей компании.")}
                </p>
              </div>
            </div>

            <p className="mt-3 text-[11px] text-muted-foreground">
              {t("Демо-режим: настройки меняются только в макете — настоящие ответы клиентам не отправляются.")}
            </p>
          </div>
        </div>

        {toast ? (
          <div
            role="status"
            className="pointer-events-none absolute inset-x-3 bottom-3 z-40 mx-auto flex max-w-md items-start gap-2 rounded-btn border border-primary/40 bg-background/95 px-3.5 py-2.5 text-[12px] text-foreground shadow-lift backdrop-blur"
          >
            <Check className="mt-0.5 size-3.5 flex-none text-primary" strokeWidth={3} aria-hidden />
            {toast}
          </div>
        ) : null}

        {/* ── тест ассистента ── */}
        {testOpen ? (
          <div className="absolute inset-0 z-[60] flex items-end justify-center bg-black/65 p-3 backdrop-blur-sm sm:items-center md:p-6">
            <div
              role="dialog"
              aria-modal="true"
              aria-labelledby="ai-test-title"
              className="relative flex max-h-full w-full max-w-md flex-col overflow-hidden rounded-card border border-border bg-card shadow-lift"
            >
              <div className="flex items-center gap-2 border-b border-border px-4 py-3">
                <span className="grid size-8 flex-none place-items-center rounded-btn bg-primary/15 text-primary" aria-hidden>
                  <i className="ri-sparkling-line text-base leading-none" />
                </span>
                <div className="min-w-0 flex-1">
                  <h3 id="ai-test-title" className="text-[13px] font-semibold leading-snug">
                    {t("Тест AI Assist")}
                  </h3>
                  <p className="text-[10.5px] text-muted-foreground">
                    {useSnippets ? t("Шаблоны подключены · {n}", { n: SNIPPET_COUNT }) : t("Шаблоны отключены")} ·{" "}
                    {outside ? t("общие знания включены") : t("только источники знаний")}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setTestOpen(false)}
                  aria-label={t("Закрыть тест AI Assist")}
                  className="grid size-8 cursor-pointer place-items-center rounded-btn text-muted-foreground transition hover:bg-muted hover:text-foreground"
                >
                  <X className="size-4" aria-hidden />
                </button>
              </div>

              <div ref={chatRef} className="flex max-h-[46vh] min-h-[220px] flex-1 flex-col gap-2 overflow-y-auto p-3.5">
                {messages.map((m) =>
                  m.role === "user" ? (
                    <p key={m.id} className="max-w-[85%] self-end rounded-btn rounded-se-sm bg-primary px-3 py-2 text-[12px] leading-relaxed text-primary-foreground">
                      {m.text}
                    </p>
                  ) : (
                    <div key={m.id} className="max-w-[88%] self-start rounded-btn rounded-ss-sm border border-border bg-background/70 px-3 py-2">
                      <p className="flex flex-wrap items-center gap-1 text-[9px] font-bold uppercase tracking-wide text-primary">
                        <i className="ri-sparkling-line text-[10px] leading-none" aria-hidden />
                        AI Assist
                        {m.source ? <span className="font-medium normal-case tracking-normal text-muted-foreground">· {m.source}</span> : null}
                      </p>
                      <p className="mt-1 text-[12px] leading-relaxed text-foreground">{m.text}</p>
                      {m.general ? (
                        <p className="mt-1.5 flex items-start gap-1 text-[10px] leading-relaxed text-amber-600">
                          <i className="ri-error-warning-line mt-px text-[11px] leading-none" aria-hidden />
                          {t("Ответ не из источников знаний — проверьте перед отправкой.")}
                        </p>
                      ) : null}
                    </div>
                  ),
                )}
                {pending ? (
                  <p className="self-start rounded-btn border border-border bg-background/70 px-3 py-2 text-[11px] text-muted-foreground">
                    {t("AI Assist печатает…")}
                  </p>
                ) : null}
              </div>

              <form onSubmit={send} className="flex items-end gap-2 border-t border-border p-3">
                <label className="min-w-0 flex-1">
                  <span className="sr-only">{t("Вопрос для AI Assist")}</span>
                  <input
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    placeholder={t("Спросите про тариф, оплату или подключение…")}
                    autoComplete="off"
                    className={cn(INPUT, "h-10")}
                  />
                </label>
                <button
                  type="submit"
                  disabled={!input.trim() || pending}
                  aria-label={t("Отправить вопрос")}
                  className="inline-flex h-10 flex-none cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-btn bg-primary px-3 text-[12px] font-semibold text-primary-foreground transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <i className="ri-send-plane-2-line" aria-hidden />
                  {t("Спросить")}
                </button>
              </form>
            </div>
          </div>
        ) : null}
      </div>
    </Band>
  );
}

export default AiAssistDemo;